// ── Weekly goal for To-Do habits ─────────────────────────────────────────────
// The "x / target" a habit shows for its current week. Shared by the Weekly
// Goal section in ProfileTab and the Habits home-screen widget so the two can't
// disagree again — the widget used to keep its own copy that counted a calendar
// week (shifted a day by a toISOString() UTC slip) and ignored the habit's start
// date, so moving a habit's start date to today still showed the earlier ticks
// from that week (5/7 where the app said 1/7).
import { fmt, todayStr, localDateStr } from "./dateHelpers";
import { intervalDueDaysBetween, intervalDuePerDays } from "./habitInterval";
// habitLogic imports goalOn from here too. The cycle is safe: neither side
// uses the other at module load, only inside functions.
import { isHabitExcludedOn, excludedDueDays } from "./habitLogic";
import { formatAmount } from "./habitUnits";

const DAY_MS = 86400000;

/** The habit's own start (its first day), as a local "yyyy-mm-dd". */
export function habitOriginStr(h) {
  return h.habitStartDate || localDateStr(h.createdAt) || todayStr();
}

/**
 * The habit's own period of `periodDays` containing today, stepped by `offset`
 * periods: periods are anchored to the habit's start date, not the calendar,
 * so a habit started on a Saturday has Saturday-to-Friday weeks.
 */
export function habitPeriodRange(h, periodDays, offset, todayMs) {
  const originMs = new Date(habitOriginStr(h) + "T00:00:00").getTime();
  const currentIdx = Math.max(0, Math.floor((todayMs - originMs) / (periodDays * DAY_MS)));
  const idx = Math.max(0, currentIdx + offset);
  const startMs = originMs + idx * periodDays * DAY_MS;
  const endMs = startMs + (periodDays - 1) * DAY_MS;
  return { startMs, endMs };
}

/** How many times a To-Do habit is due per 7-day week (schedule maths, not progress). */
export function todoWeeklyTarget(h, dateStr) {
  if (dateStr) h = goalOn(h, dateStr);
  if (h.frequencyType === "daily") return 7;
  // Interval habits: the actual due days in this 7-day week (an every-2-days
  // habit has 3 or 4; an every-2-weeks one 1 or 0). Without a week start, the
  // average.
  if (h.frequencyType === "interval") {
    if (!dateStr) return intervalDuePerDays(h, 7);
    const end = new Date(dateStr + "T00:00:00"); end.setDate(end.getDate() + 6);
    return intervalDueDaysBetween(h, dateStr, fmt(end));
  }
  if (h.frequencyType === "count") return h.weeklyCount || 1;
  if (h.frequencyType === "specific") return (h.days || []).length || 1;
  if (h.frequencyType === "monthly") {
    // "Times/month" habits store monthlyCount, not monthDates.
    if (h.monthlyMode === "count") return Math.max(1, Math.round((h.monthlyCount || 1) / 4.33));
    return Math.max(1, Math.round((h.monthDates || []).length / 4.33));
  }
  if (h.frequencyType === "yearly") return Math.max(1, Math.round((h.yearlyCount || 1) / 52));
  return 1;
}

/** Whether a To-Do habit was fully done on `dateStr` (all of its daily count logged). */
export function isTodoCompleteOn(h, habitLogs, dateStr) {
  const v = habitLogs?.[dateStr]?.[h.id];
  const target = goalOn(h, dateStr).dailyCount || 1;
  return typeof v === "number" ? v >= target : !!v;
}

/**
 * Days a To-Do habit was completed between fromMs and toMs, never before its
 * start or after today. Excluded dates (days off) don't count, the same way
 * every target leaves them out (minusExcluded).
 */
export function todoCompletedInRange(h, habitLogs, fromMs, toMs, todayMs) {
  const originMs = new Date(habitOriginStr(h) + "T00:00:00").getTime();
  let count = 0;
  for (let ms = Math.max(fromMs, originMs); ms <= Math.min(toMs, todayMs); ms += DAY_MS) {
    const ds = fmt(new Date(ms));
    if (isHabitExcludedOn(h, ds)) continue;
    if (isTodoCompleteOn(h, habitLogs, ds)) count++;
  }
  return count;
}

/**
 * A schedule target (`base`, e.g. 7 for a daily habit's week) less the
 * excluded dates in [fromMs, toMs] that would otherwise be due. Never below
 * 0 — a week that's all days off has nothing to aim for.
 */
export function minusExcluded(h, base, fromMs, toMs) {
  return Math.max(0, base - excludedDueDays(h, fmt(new Date(fromMs)), fmt(new Date(toMs))));
}

/** The current week's progress for a To-Do habit, exactly as Weekly Goal shows it. */
export function todoWeeklyProgress(h, habitLogs, today = todayStr()) {
  const todayMs = new Date(today + "T00:00:00").getTime();
  const { startMs, endMs } = habitPeriodRange(h, 7, 0, todayMs);
  return {
    completed: todoCompletedInRange(h, habitLogs, startMs, endMs, todayMs),
    target: minusExcluded(h, todoWeeklyTarget(h, fmt(new Date(startMs))), startMs, endMs),
  };
}

// ── Goal changes that keep history ───────────────────────────────────────────
// A habit's own number fields (weeklyCount, ...) always hold the goal that is in
// effect NOW, so everything that looks at "this week" / "today" reads them as it
// always did. Editing one of them doesn't overwrite it: the change is parked in
// `goalPending` ({ from, goal }) and applied when the next period starts
// (applyDueGoalChanges, run on load / remote sync / day change). Applying it
// moves the old numbers into `goalHistory` ([{ until, goal }], oldest first;
// `until` is the first day the NEXT goal applied), so anything that judges a past
// week or day asks goalOn(habit, thatDate) and sees the goal of its time.
// A habit that never changed its goal has neither field: goalOn returns the
// habit itself, so its stats are exactly what they were before this existed.
// Besides the numbers, the "which days" lists are goals too: `days` (a
// specific-days habit's weekdays), `monthDates` (dates of the month) and
// `yearDates` ("M-D"). Only changes that keep the habit's type are goal
// changes; switching type (or a monthly/yearly habit between counted and
// dates) is a correction that recalculates the whole history, as before.
// A habit's unit (habitUnits.js) is part of its daily goal: `unit` and
// `unitLabel` go with `dailyCount` (the "daily" switch), so a past day keeps
// the unit it was logged in when the new-goal switch is on.
export const GOAL_FIELDS = ["weeklyCount", "monthlyCount", "yearlyCount", "dailyCount", "unit", "unitLabel", "days", "monthDates", "yearDates"];
const LIST_FIELDS = ["days", "monthDates", "yearDates"];
const DAILY_FIELDS = ["dailyCount", "unit", "unitLabel"];
// Unset unit fields read as these (habitUnits.unitOf / unitWord).
const STRING_DEFAULTS = { unit: "count", unitLabel: "" };
// The edit form shows these when the habit has none saved; comparing against
// them keeps "never touched" from reading as a change.
const GOAL_DEFAULTS = { dailyCount: 1, weeklyCount: 3, monthlyCount: 10, yearlyCount: 12 };

/** The habit's frequency type, with the counted/dates mode for monthly and yearly. */
export function shapeOf(h) {
  const ft = h?.frequencyType || "daily";
  if (ft === "monthly") return h.monthlyMode === "count" ? "monthly:count" : "monthly:dates";
  if (ft === "yearly") return (h.yearlyMode || "count") === "count" ? "yearly:count" : "yearly:dates";
  return ft;
}

/** The goal fields that matter for this habit's current shape (To-Do habits only). */
export function relevantGoalFields(h) {
  if (!h || h.habitType === "nottodo" || h.habitType === "limited") return [];
  const out = [...DAILY_FIELDS];
  const shape = shapeOf(h);
  if (shape === "count") out.push("weeklyCount");
  if (shape === "specific") out.push("days");
  if (shape === "monthly:count") out.push("monthlyCount");
  if (shape === "monthly:dates") out.push("monthDates");
  if (shape === "yearly:count") out.push("yearlyCount");
  if (shape === "yearly:dates") out.push("yearDates");
  return out;
}

// Unsaved times-per-day / day lists are recorded as what the app reads for
// them (1, []), since a missing key in a history entry would fall through to
// the habit's NEW value. (Firestore drops undefined, so it can't be stored.)
const goalSnapshot = (h) => {
  const g = {};
  for (const f of GOAL_FIELDS) {
    const v = h[f] !== undefined ? h[f] : f === "dailyCount" ? 1 : f in STRING_DEFAULTS ? STRING_DEFAULTS[f] : LIST_FIELDS.includes(f) ? [] : undefined;
    if (v !== undefined) g[f] = Array.isArray(v) ? [...v] : v;
  }
  return g;
};
const COUNT_FIELDS = ["weeklyCount", "monthlyCount", "yearlyCount"];

const normGoal = (k, v) => LIST_FIELDS.includes(k)
  ? JSON.stringify([...(v || [])].map(String).sort())
  : k in STRING_DEFAULTS
    ? (String(v ?? "").trim() || STRING_DEFAULTS[k])
    : (v === undefined || v === null || v === "" ? GOAL_DEFAULTS[k] : Number(v));
const sameGoal = (k, a, b) => normGoal(k, a) === normGoal(k, b);
const groupOf = (k) => (DAILY_FIELDS.includes(k) ? "daily" : "freq");

/** The habit as it was on `dateStr` — the same object when its goal never changed. */
export function goalOn(h, dateStr) {
  const hist = h?.goalHistory;
  if (!hist || !hist.length || !dateStr) return h;
  for (const seg of hist) if (dateStr < seg.until) return { ...h, ...seg.goal };
  return h;
}

/** The habit with its pending goal shown — what the edit form should start from. */
export function withPendingGoal(h) {
  return h?.goalPending?.goal ? { ...h, ...h.goalPending.goal } : h;
}

/** First day a changed goal applies: the 1st of next month for monthly habits, else the habit's next 7-day week. */
export function nextGoalStart(h, today = todayStr()) {
  if (h.frequencyType === "monthly") {
    const d = new Date(today + "T00:00:00");
    return fmt(new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }
  const todayMs = new Date(today + "T00:00:00").getTime();
  return fmt(new Date(habitPeriodRange(h, 7, 1, todayMs).startMs));
}

/**
 * UI-only fields the edit form starts with (all stripped on save):
 * `_goalNote` (switch info text; empty = no switch: the habit has no past),
 * `_goalShape` (switch hides once the type changes), `_goalOrig` (what each
 * goal field was when the form opened, so a switch shows only after a change),
 * `_goalNewDaily` / `_goalNewFreq` (each switch's position; ON when that part
 * already has a change waiting to start).
 */
export function goalFormFlags(h, today = todayStr()) {
  const pend = h?.goalPending?.goal || {};
  return {
    _goalNote: goalChangeNote(h, today),
    _goalShape: shapeOf(h),
    _goalOrig: goalSnapshot(withPendingGoal(h) || {}),
    _goalNewDaily: DAILY_FIELDS.some(k => k in pend),
    _goalNewFreq: Object.keys(pend).some(k => !DAILY_FIELDS.includes(k)),
    // "Starts" chips under each switch: false = next period (the default), true = today.
    _goalNowDaily: false,
    _goalNowFreq: false,
    _goalNextFrom: h?.goalPending?.from || (h ? nextGoalStart(h, today) : ""),
  };
}

const shortDate = (ds) => new Date(ds + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** One line under the "Starts" chips saying exactly what happens. */
export function goalPreview(form, field, now) {
  const orig = form?._goalOrig || {};
  const from = form?._goalNextFrom ? shortDate(form._goalNextFrom) : "next week";
  const unit = field === "monthlyCount" ? "month" : "week";
  if (DAILY_FIELDS.includes(field)) {
    // The daily goal with its unit: "8 glasses a day", "3× a day", "1h a day".
    const daily = (g) => {
      const n = normGoal("dailyCount", g.dailyCount);
      return normGoal("unit", g.unit) === "count" ? `${n}× a day` : `${formatAmount(g, n)} a day`;
    };
    const was = daily(orig);
    const next = daily(form);
    return now ? `${next} from today. Earlier days stay at ${was}.` : `Stays ${was} until ${from}, then ${next}.`;
  }
  const per = { weeklyCount: "a week", monthlyCount: "a month", yearlyCount: "a year", dailyCount: "a day" }[field];
  if (per) {
    const was = `${normGoal(field, orig[field])}× ${per}`;
    const next = `${normGoal(field, form[field])}× ${per}`;
    if (field === "dailyCount") return now ? `${next} from today. Earlier days stay at ${was}.` : `Stays ${was} until ${from}, then ${next}.`;
    return now ? `This ${unit} already counts toward ${next}.` : `This ${unit} stays at ${was}. ${next} starts ${from}.`;
  }
  const what = field === "days" ? "days" : "dates";
  return now ? `The new ${what} count from today. Earlier days keep the old ${what}.` : `The new ${what} start ${from}. Until then, the old ${what} count.`;
}

/** Whether the form has changed `field` since it opened (defaults count as "unchanged"). */
export function goalFieldChanged(form, field) {
  if (!form?._goalOrig) return false;
  return !sameGoal(field, form[field], form._goalOrig[field]);
}

/**
 * Turns an edit form into what to save for the goal fields. Returns a patch to
 * merge over the cleaned form. `startNew` is { daily, freq } — the two
 * "Start a new goal" switches (a plain true/false sets both).
 *  - Field unchanged: anything already parked for it stays parked.
 *  - Changed, its switch ON: held back at its current value; the change is
 *    parked in `goalPending` until the next period.
 *  - Changed, its switch OFF (or a habit that starts today — no past to
 *    protect): a correction. Saved as is, and removed from the goal history,
 *    so every past day is judged by the new value.
 */
export function resolveGoalEdit(form, prev, today = todayStr(), startNew = false) {
  if (!prev) return {};
  // A type switch (or to-do <-> restricted) isn't a goal change: the whole
  // history is recalculated with the new type, so any goal history goes.
  const typeOf = (h) => `${h.habitType || "todo"}|${shapeOf(withPendingGoal(h))}`;
  if (form.habitType !== undefined && typeOf(form) !== typeOf(prev)) return { goalPending: undefined, goalHistory: undefined };
  const fields = relevantGoalFields(prev);
  if (!fields.length) return {};
  const sn = typeof startNew === "object" && startNew ? startNew : { daily: !!startNew, freq: !!startNew };
  const startsNow = [];
  const immediate = habitOriginStr(prev) >= today;
  const pending = { ...(prev.goalPending?.goal || {}) };
  const corrected = [];
  for (const f of fields) {
    if (form[f] === undefined) continue;
    const eff = f in pending ? pending[f] : prev[f];
    if (sameGoal(f, form[f], eff)) continue;
    const val = f === "yearlyCount" ? Math.max(1, parseInt(form[f], 10) || 1) : Array.isArray(form[f]) ? [...form[f]] : form[f];
    if (!immediate && sn[groupOf(f)] && sn[groupOf(f) + "Now"]) { startsNow.push(f); delete pending[f]; }
    else if (!immediate && sn[groupOf(f)]) pending[f] = val;
    else { corrected.push(f); delete pending[f]; }
  }
  const patch = {};
  for (const f of Object.keys(pending)) {
    if (sameGoal(f, pending[f], prev[f])) delete pending[f];
    else patch[f] = prev[f]; // held back until `from`
  }
  patch.goalPending = Object.keys(pending).length
    ? { from: prev.goalPending?.from || nextGoalStart(prev, today), goal: pending }
    : undefined;
  if (corrected.length && prev.goalHistory?.length) {
    const hist = prev.goalHistory
      .map(seg => { const goal = { ...seg.goal }; for (const f of corrected) delete goal[f]; return { ...seg, goal }; })
      .filter(seg => Object.keys(seg.goal).length);
    patch.goalHistory = hist.length ? hist : undefined;
  }
  if (startsNow.length) {
    // New goal from today: saved as is, the old value goes into history.
    // Day-by-day goals (times per day, the day lists) change from today. A
    // "N times a week/month/year" goal covers the whole period you're in, so
    // it changes from the start of this period — this period then counts
    // toward the new number (no part-week fractions).
    const todayMs = new Date(today + "T00:00:00").getTime();
    const d = new Date(today + "T00:00:00");
    const periodStart = prev.frequencyType === "monthly"
      ? fmt(new Date(d.getFullYear(), d.getMonth(), 1))
      : fmt(new Date(habitPeriodRange(prev, 7, 0, todayMs).startMs));
    const untilOf = (f) => (COUNT_FIELDS.includes(f) ? periodStart : today);
    const old = goalSnapshot(prev);
    const origin = habitOriginStr(prev);
    const segs = [...new Set(startsNow.map(untilOf))].filter(u => u > origin).sort().map(u => {
      const goal = {};
      for (const f of startsNow) if (untilOf(f) >= u && old[f] !== undefined) goal[f] = old[f];
      return { until: u, goal };
    }).filter(seg => Object.keys(seg.goal).length);
    const base = "goalHistory" in patch ? (patch.goalHistory || []) : (prev.goalHistory || []);
    const hist = [...base, ...segs].sort((a, b) => (a.until < b.until ? -1 : a.until > b.until ? 1 : 0));
    patch.goalHistory = hist.length ? hist : undefined;
  }
  return patch;
}

/** Applies every pending goal whose day has come. Same array back when nothing was due. */
export function applyDueGoalChanges(habits, today = todayStr()) {
  if (!Array.isArray(habits)) return habits;
  let changed = false;
  const out = habits.map(h => {
    if (!h?.goalPending || !h.goalPending.from || h.goalPending.from > today) return h;
    changed = true;
    const { goalPending, ...rest } = h;
    return {
      ...rest,
      ...goalPending.goal,
      goalHistory: [...(h.goalHistory || []), { until: goalPending.from, goal: goalSnapshot(h) }],
    };
  });
  return changed ? out : habits;
}

/** Badge text for the "Start a new goal" (i); empty when the habit has no past to protect. */
export function goalChangeNote(h, today = todayStr()) {
  if (!h || !relevantGoalFields(h).length || habitOriginStr(h) >= today) return "";
  return `On: keeps your past progress and graphs as they are. You pick when the new goal starts.\nOff: updates your past progress with this change.`;
}
